/**
 * The three designed states of the search screen: nothing matched, nothing
 * asked yet, and the guest's metadata tier.
 *
 * The rule they share, from the design system: say what happened, say what it
 * means for the work in hand, and give the one action that moves it forward.
 * No empty without a first step, no denial without a reason.
 *
 * **Every figure on these panels is counted by the API.** Where a count is not
 * available the row carrying it is omitted, never printed with a plausible
 * number: an alternative that promises 14 results and finds none is worse than
 * no alternative at all.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, api } from '../../lib/api'
import type { LandingFacts, SearchAlternatives, Facets } from '../../lib/api'
import type { SearchState } from './model'
import { n } from './model'

// --- 3.2 no results ---------------------------------------------------------

export function NoResults({
  state,
  alternatives,
  onApply,
  reportParams,
}: {
  state: SearchState
  alternatives: SearchAlternatives | undefined
  onApply: (params: Record<string, string>) => void
  /** The search exactly as it was run, filed with the report. */
  reportParams: Record<string, string>
}) {
  const widen = alternatives?.widen ?? localWiden(state)

  return (
    <div className="mx-auto w-full max-w-[1000px] px-4 py-8 sm:px-[22px]">
      <div className="mb-5 flex max-w-[520px] items-center gap-2.5">
        <div className="flex h-[34px] flex-1 items-center gap-[9px] rounded-[--radius-md] border border-rule2 px-[11px]">
          <span className="font-mono text-[12px] text-ink3" aria-hidden="true">
            ⌕
          </span>
          <span className="truncate text-[15.5px]">{state.q}</span>
        </div>
        <div className="font-mono text-[10px] uppercase text-ink2">
          {alternatives ? `${n(alternatives.exact)} exact` : '0 results'}
        </div>
      </div>

      <h2 className="m-0 mb-2 text-[26px] font-semibold leading-[1.15] tracking-[-0.012em]">
        Nothing matched those words exactly.
      </h2>
      <p className="m-0 mb-[22px] max-w-[66ch] text-[16.5px] leading-[1.55] text-ink2">
        On scanned editions that usually means the words are on the page but were misread.
        {!alternatives
          ? ' The archive can look again by shape, by meaning and by sound; those counts are not available from this server, so nothing is promised here that has not been counted.'
          : alternatives.alternatives.length > 0
            ? ' Below are the ways the archive can look again. Each is a real query, and each says how many it would find.'
            : ' The archive has already looked again by shape, by meaning and by sound. None of those found anything either, so there is nothing to offer that would not be a guess.'}
      </p>

      {alternatives && alternatives.alternatives.length > 0 && (
        <div className="flex flex-col gap-px overflow-hidden rounded-[--radius-md] border border-n300 bg-n300">
          {alternatives.alternatives.map((alternative, index) => {
            const first = index === 0
            return (
              <div
                key={alternative.layer}
                className="flex flex-wrap items-center gap-3.5 px-[15px] py-[13px]"
                style={{ background: first ? 'var(--jl-accent-100)' : 'var(--color-bg)' }}
              >
                <div
                  className={`w-[74px] flex-none font-mono text-[9.5px] uppercase tracking-[0.12em] ${first ? 'text-accent' : 'text-ink2'}`}
                >
                  {alternative.layer}
                </div>
                <div className="w-[200px] flex-none font-mono text-[13px] text-ink">
                  {alternative.query}
                </div>
                <div className="min-w-[220px] flex-1 text-[14px] leading-[1.4] text-ink2">
                  {alternative.note}
                  {/*
                    Three examples, not thirty: the point is to show the reader
                    the *kind* of damage the layer repairs, and a paragraph of
                    mangled headlines makes the row unreadable without making
                    the point any better.
                  */}
                  {alternative.finds && alternative.finds.length > 0 && (
                    <> Finds {alternative.finds.slice(0, 3).join(', ')}.</>
                  )}
                </div>
                <div className="flex-none font-mono text-[11px] text-ink">
                  {n(alternative.count)} results
                </div>
                <button
                  type="button"
                  onClick={() => onApply(alternative.params)}
                  className={`flex-none rounded-[--radius-md] px-3 py-[5px] text-[13px] font-semibold ${
                    first ? 'bg-accent text-paper' : 'border border-rule2 text-accent'
                  }`}
                >
                  Try
                </button>
              </div>
            )
          })}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-[22px] border-t border-n300 pt-4">
        <div className="min-w-[280px] flex-1">
          <div className="mb-[7px] text-[11px] font-semibold uppercase tracking-[0.11em] text-ink2">
            Or widen what you asked for
          </div>
          <div className="flex flex-wrap gap-[7px]">
            {widen.map((option) => {
              // A widen option the server counted says what it would find,
              // including when the answer is nothing: a reader is owed that
              // before spending a click on it.
              const counted = Boolean(alternatives)
              const futile = counted && option.count === 0
              return (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => onApply(option.params)}
                  title={futile ? 'This finds nothing either' : undefined}
                  className={`flex items-center gap-2 rounded-[--radius-md] border border-rule2 px-2.5 py-1 text-[13px] ${futile ? 'text-ink3' : 'text-ink'}`}
                >
                  {option.label}
                  {counted && (
                    <span className="font-mono text-[10px] text-ink2">{n(option.count)}</span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
        <div className="w-[250px] flex-none">
          <div className="mb-[7px] text-[11px] font-semibold uppercase tracking-[0.11em] text-ink2">
            Still nothing?
          </div>
          <ReportMissing query={state.q} params={reportParams} />
        </div>
      </div>
    </div>
  )
}

/**
 * Report a passage the reader knows exists.
 *
 * It files a real review item, so it says so and then proves it by printing
 * the id it was given: a receipt in the mono, which is the only thing that
 * turns "we have recorded this" from a reassurance into a fact the reader can
 * quote back. The search and its filters travel with the note, because a
 * report that does not say what was asked is not actionable.
 *
 * A guest is refused by the server, and the server's own sentence is shown
 * rather than one invented here: the denial is about who you are, so it is
 * paired with the way to stop being anonymous and never with a licence pitch.
 */
function ReportMissing({ query, params }: { query: string; params: Record<string, string> }) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [filed, setFiled] = useState<{ id: string; status: string } | null>(null)
  const [denied, setDenied] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  async function send(event: React.FormEvent) {
    event.preventDefault()
    setSending(true)
    setDenied(null)
    setFailed(null)
    try {
      setFiled(
        await api.post<{ id: string; status: string }>('/review/report', {
          query,
          params,
          note: note.trim() || null,
        }),
      )
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) setDenied(error.detail)
      else setFailed(error instanceof Error ? error.message : 'The report could not be filed.')
    } finally {
      setSending(false)
    }
  }

  if (filed) {
    return (
      <div>
        <p className="m-0 mb-1.5 text-[13.5px] leading-[1.45] text-ink">
          Recorded as a review item. An editor sees it in the queue.
        </p>
        <div className="font-mono text-[11px] text-ink2">
          {filed.id} · {filed.status}
        </div>
      </div>
    )
  }

  if (denied) {
    return (
      <div>
        <p className="m-0 mb-2 text-[13.5px] leading-[1.45] text-ink2">{denied}</p>
        <Link to="/login" className="text-[13.5px]">
          Sign in
        </Link>
      </div>
    )
  }

  return (
    <div>
      <p className="m-0 mb-2 text-[13.5px] leading-[1.45] text-ink2">
        Report a passage you know exists. It becomes a review item with your note attached.
      </p>
      {open ? (
        <form onSubmit={(event) => void send(event)}>
          <label className="sr-only" htmlFor="missing-note">
            What you expected to find
          </label>
          <textarea
            id="missing-note"
            autoFocus
            rows={3}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="What you expected to find, and where"
            className="input mb-2 resize-y text-[13.5px]"
          />
          <div className="flex items-center gap-2">
            <button type="submit" className="btn-primary" disabled={sending}>
              {sending ? 'Sending…' : 'Send'}
            </button>
            <button type="button" className="btn-ghost text-[13px]" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
          {failed && <p className="mt-2 text-[13px] text-mark">{failed}</p>}
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="bg-transparent p-0 text-[13.5px] text-accent underline underline-offset-2"
        >
          Report a missing passage
        </button>
      )}
    </div>
  )
}

/**
 * What can be widened without asking the server: each of these is a filter the
 * reader themselves set, so dropping it needs no count to be honest. Nothing
 * here claims how many results it would find.
 */
function localWiden(state: SearchState): SearchAlternatives['widen'] {
  const options: SearchAlternatives['widen'] = []
  const words = state.q.trim().split(/\s+/).filter(Boolean)
  if (words.length > 1) {
    options.push({
      label: `Drop “${words[words.length - 1]}”`,
      params: { q: words.slice(0, -1).join(' ') },
      count: 0,
    })
  }
  if (state.min_confidence) {
    options.push({ label: 'Lower confidence to 50%', params: { min_confidence: '' }, count: 0 })
  }
  if (state.section) {
    options.push({ label: 'All sections', params: { section: '' }, count: 0 })
  }
  if (state.types.length > 0) {
    options.push({ label: 'All content types', params: { types: '' }, count: 0 })
  }
  if (state.date_from || state.date_to) {
    options.push({ label: 'All years', params: { date_from: '', date_to: '' }, count: 0 })
  }
  if (state.mode !== 'semantic') {
    options.push({ label: 'Search by meaning', params: { mode: 'semantic' }, count: 0 })
  }
  return options
}

// --- 3.3 empty, before the first search -------------------------------------

export interface Starter {
  kind: string
  label: string
  note: string
  params: Record<string, string>
}

export function EmptyState({
  facts,
  facets,
  starters,
  coverage,
  gaps,
  onApply,
}: {
  facts: LandingFacts | undefined
  facets: Facets | undefined
  starters: Starter[]
  coverage: [string, string][]
  gaps: [string, string][]
  onApply: (params: Record<string, string>) => void
}) {
  const span =
    facts?.year_range ??
    (facets?.earliest && facets?.latest
      ? `${facets.earliest.slice(0, 4)}–${facets.latest.slice(0, 4)}`
      : null)

  return (
    <div className="mx-auto w-full max-w-[620px] px-[22px] py-10">
      {(facts?.publication || span) && (
        <div className="mb-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink2">
          {[facts?.publication, span].filter(Boolean).join(' · ')}
        </div>
      )}
      <h2 className="m-0 mb-[7px] text-[23px] font-semibold leading-[1.2] tracking-[-0.012em]">
        {facts
          ? `${n(facts.editions)} ${facts.editions === 1 ? 'edition' : 'editions'}. ${n(facts.pages)} ${facts.pages === 1 ? 'page' : 'pages'}.`
          : 'The archive is ready.'}
      </h2>
      <p className="m-0 mb-[18px] text-[16px] leading-[1.5] text-ink2">
        Ask in your own words, or start from a date.
      </p>

      {starters.length > 0 && (
        <div className="flex flex-col gap-[9px]">
          {starters.map((starter) => (
            <div
              key={starter.kind}
              className="flex items-baseline gap-2.5 border-b border-n200 pb-2"
            >
              <div className="w-[62px] flex-none font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2">
                {starter.kind}
              </div>
              <button
                type="button"
                onClick={() => onApply(starter.params)}
                className="flex-1 bg-transparent p-0 text-left text-[15px] text-accent underline underline-offset-2"
              >
                {starter.label}
              </button>
              <div className="flex-none font-mono text-[10px] text-ink2">{starter.note}</div>
            </div>
          ))}
        </div>
      )}

      {(coverage.length > 0 || gaps.length > 0) && (
        <div className="mt-[18px] flex gap-5 border-t border-n300 pt-3.5">
          {coverage.length > 0 && (
            <div>
              <div className="mb-[5px] font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2">
                Coverage
              </div>
              <div className="font-mono text-[12px] leading-[1.6] text-ink">
                {coverage.map(([left, right]) => (
                  <div key={left}>
                    {left}&nbsp;&nbsp;{right}
                  </div>
                ))}
              </div>
            </div>
          )}
          {gaps.length > 0 && (
            <div>
              <div className="mb-[5px] font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2">
                Gaps
              </div>
              <div className="font-mono text-[12px] leading-[1.6] text-ink">
                {gaps.map(([left, right]) => (
                  <div key={left}>
                    {left}&nbsp;&nbsp;{right}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// --- 3.4 the guest's tier ---------------------------------------------------

/**
 * The two denials are never the same screen. This is the one about who you
 * are, so it says what the result *is* first, then what a licence unlocks, and
 * it prints the tier line by line rather than leaving a guest to discover the
 * boundary by hitting it.
 */
export function GuestTierBanner() {
  return (
    <div className="border-b border-n300 bg-surface px-[22px] py-4">
      <div className="mb-3 text-[15.5px] leading-[1.5] text-ink">
        These results exist and are described accurately. The page image and the full text are what
        your library&rsquo;s licence unlocks.
      </div>
      <div className="flex flex-wrap items-center gap-[9px]">
        <Link to="/login" className="btn-primary no-underline">
          Sign in through your library
        </Link>
        <a href="/docs/handbook/" className="text-[13.5px]">
          Check if your institution subscribes
        </a>
      </div>
      <div className="mt-3 font-mono text-[9.5px] uppercase leading-[1.7] text-ink2">
        Guest tier gives: headline · byline · date · section · page · 140-char snippet
        <br />
        Withheld: page image · full text · clippings · export
      </div>
    </div>
  )
}
